///
/// EarthBrowser
///
/// Created by Matt Giger
/// Copyright (c) 2025 Geodata Labs
///

import { type geographic, wgs } from "@/earthbrowser/utils/geographic"
import { TileId } from "@/earthbrowser/activities/EarthActivity/tilecache/TileId"
import { ElevationLoader } from "@/earthbrowser/utils/loaders/ElevationLoader"
import { trig } from "@stovokor/math/math"
import { logger } from "@/utils/LogDispatcher"

const cElevationServerHost = "https://tiles.earthbrowser.com"

// Contour line segment from CONREC algorithm
export interface ContourSegment {
	x1: number
	y1: number
	x2: number
	y2: number
	level: number
}

// Elevation data grid with metadata
export interface ElevationGrid {
	data: number[][]
	width: number
	height: number
	minLon: number
	maxLon: number
	minLat: number
	maxLat: number
	minElev: number
	maxElev: number
	// Center coordinate (the target location, e.g. volcano)
	centerLon: number
	centerLat: number
	// Optimal crop radius as fraction of grid size (0-1), based on volcano footprint analysis
	optimalCropRadius: number
}

/**
 * Service for loading elevation tiles and generating contour data
 */
export class ElevationContourService {
	/**
	 * Calculate the angular span (in radians) for a given distance in km at a latitude
	 */
	private static kmToRadians(km: number, latitude: number): { lonSpan: number; latSpan: number } {
		// Earth's radius in km
		const R = wgs.a
		// Latitude span is constant
		const latSpan = km / R
		// Longitude span varies with latitude
		const lonSpan = km / (R * Math.cos(latitude))
		return { lonSpan, latSpan }
	}

	/**
	 * Calculate the tile level that results in no more than maxTiles tiles
	 * covering a region of radiusKm around a coordinate
	 */
	static calculateOptimalTileLevel(
		coord: geographic,
		radiusKm: number,
		maxTiles: number = 10
	): number {
		const { lonSpan, latSpan } = this.kmToRadians(radiusKm, coord.latitude)

		// Try zoom levels from high (detailed) to low (coarse)
		// Elevation tiles go from level 4 to 12
		for (let level = 12; level >= 4; level--) {
			const tiles = this.getTilesForRegion(coord, lonSpan, latSpan, level)
			if (tiles.length <= maxTiles) {
				return level
			}
		}

		return 4 // Minimum level
	}

	/**
	 * Get all TileIds that cover a rectangular region around a coordinate
	 */
	static getTilesForRegion(
		coord: geographic,
		lonSpan: number,
		latSpan: number,
		level: number
	): TileId[] {
		const rows = 1 << level
		const cols = 1 << (level + 1)

		// Calculate bounds
		const minLon = coord.longitude - lonSpan
		const maxLon = coord.longitude + lonSpan
		const minLat = coord.latitude - latSpan
		const maxLat = coord.latitude + latSpan

		// Convert to tile coordinates
		const minX = Math.floor(((minLon + trig.pi) / trig.two_pi) * cols)
		const maxX = Math.floor(((maxLon + trig.pi) / trig.two_pi) * cols)
		const minY = Math.floor(((trig.half_pi - maxLat) / trig.pi) * rows)
		const maxY = Math.floor(((trig.half_pi - minLat) / trig.pi) * rows)

		const tiles: TileId[] = []
		for (let y = Math.max(0, minY); y <= Math.min(rows - 1, maxY); y++) {
			for (let x = Math.max(0, minX); x <= Math.min(cols - 1, maxX); x++) {
				tiles.push(new TileId(level, y, x))
			}
		}

		return tiles
	}

	/**
	 * Get tiles covering a 40km region around a coordinate
	 */
	static getTilesForRegionAroundCoord(coord: geographic, radiusKm: number = 40): { tiles: TileId[]; level: number } {
		const level = this.calculateOptimalTileLevel(coord, radiusKm, 10)
		const { lonSpan, latSpan } = this.kmToRadians(radiusKm, coord.latitude)
		const tiles = this.getTilesForRegion(coord, lonSpan, latSpan, level)
		return { tiles, level }
	}

	/**
	 * Load elevation data from a tile
	 */
	static async loadElevationTile(tileId: TileId): Promise<Uint16Array | undefined> {
		const url = `${cElevationServerHost}/elevation/${tileId.z}/${tileId.y}/${tileId.x}.webp`
		const loader = new ElevationLoader(url)
		return await loader.loadData()
	}

	/**
	 * Combine multiple elevation tiles into a single grid
	 */
	static async loadElevationGrid(
		coord: geographic,
		_radiusKm: number = 40
	): Promise<ElevationGrid | undefined> {
		const { tiles, level } = this.getTilesForRegionAroundCoord(coord)

		if (tiles.length === 0) {
			logger.warn("No tiles found for region")
			return undefined
		}

		// Load all tiles in parallel
		const tileDataPromises = tiles.map(async tile => ({
			tile,
			data: await this.loadElevationTile(tile)
		}))

		const loadedTiles = await Promise.all(tileDataPromises)
		const validTiles = loadedTiles.filter(t => t.data !== undefined)

		if (validTiles.length === 0) {
			logger.warn("No valid elevation tiles loaded")
			return undefined
		}

		// Determine grid bounds from tiles
		let minTileX = Infinity, maxTileX = -Infinity
		let minTileY = Infinity, maxTileY = -Infinity
		for (const { tile } of validTiles) {
			minTileX = Math.min(minTileX, tile.x)
			maxTileX = Math.max(maxTileX, tile.x)
			minTileY = Math.min(minTileY, tile.y)
			maxTileY = Math.max(maxTileY, tile.y)
		}

		// Each tile is typically 256x256 pixels
		const tileSize = 256
		const gridWidth = (maxTileX - minTileX + 1) * tileSize
		const gridHeight = (maxTileY - minTileY + 1) * tileSize

		// Create combined grid - yield to prevent UI freeze
		await new Promise(resolve => setTimeout(resolve, 0))

		const data: number[][] = Array.from({ length: gridHeight }, () =>
			Array.from({ length: gridWidth }, () => 0)
		)

		let minElev = Infinity
		let maxElev = -Infinity

		// Place each tile's data into the grid - yield between tiles
		for (const { tile, data: tileData } of validTiles) {
			if (!tileData) continue

			const tileOffsetX = (tile.x - minTileX) * tileSize
			const tileOffsetY = (tile.y - minTileY) * tileSize

			for (let y = 0; y < tileSize && y < Math.sqrt(tileData.length); y++) {
				for (let x = 0; x < tileSize && x < Math.sqrt(tileData.length); x++) {
					const idx = y * tileSize + x
					if (idx < tileData.length) {
						// Elevation is stored as meters, values > 32768 are invalid
						let elev = tileData[idx]
						if (elev >= 32768) {
							elev = 0 // Ocean or no data
						}
						data[tileOffsetY + y][tileOffsetX + x] = elev
						if (elev > 0) {
							minElev = Math.min(minElev, elev)
							maxElev = Math.max(maxElev, elev)
						}
					}
				}
			}

			// Yield to UI between tiles to prevent freezing
			await new Promise(resolve => setTimeout(resolve, 0))
		}

		// Calculate geographic bounds
		const rows = 1 << level
		const cols = 1 << (level + 1)
		const deltaLon = trig.two_pi / cols
		const deltaLat = trig.pi / rows

		const minLon = minTileX * deltaLon - trig.pi
		const maxLon = (maxTileX + 1) * deltaLon - trig.pi
		const maxLat = trig.half_pi - minTileY * deltaLat
		const minLat = trig.half_pi - (maxTileY + 1) * deltaLat

		if (minElev === Infinity) minElev = 0
		if (maxElev === -Infinity) maxElev = 0

		// Calculate optimal crop radius based on volcano footprint
		const optimalCropRadius = this.calculateVolcanoFootprint(
			data, gridWidth, gridHeight,
			((coord.longitude - minLon) / (maxLon - minLon)) * gridWidth,
			((maxLat - coord.latitude) / (maxLat - minLat)) * gridHeight,
			minElev, maxElev
		)

		return {
			data,
			width: gridWidth,
			height: gridHeight,
			minLon,
			maxLon,
			minLat,
			maxLat,
			minElev,
			maxElev,
			centerLon: coord.longitude,
			centerLat: coord.latitude,
			optimalCropRadius
		}
	}

	/**
	 * Calculate optimal crop radius based on volcano's footprint
	 * Analyzes the elevation profile to find where the volcano "ends" and surrounding terrain begins
	 * @returns Optimal radius as fraction of grid size (0.15 to 0.5)
	 */
	private static calculateVolcanoFootprint(
		data: number[][],
		width: number,
		height: number,
		centerX: number,
		centerY: number,
		minElev: number,
		maxElev: number
	): number {
		const elevRange = maxElev - minElev
		if (elevRange <= 0) return 0.35 // Default fallback

		// Get peak elevation at center
		const cx = Math.round(Math.max(0, Math.min(width - 1, centerX)))
		const cy = Math.round(Math.max(0, Math.min(height - 1, centerY)))
		const peakElev = data[cy]?.[cx] ?? maxElev

		// Sample the perimeter of the grid to estimate base elevation
		const perimeterSamples: number[] = []
		const sampleStep = Math.max(1, Math.floor(Math.min(width, height) / 20))

		// Top and bottom edges
		for (let x = 0; x < width; x += sampleStep) {
			if (data[0]?.[x] !== undefined) perimeterSamples.push(data[0][x])
			if (data[height - 1]?.[x] !== undefined) perimeterSamples.push(data[height - 1][x])
		}
		// Left and right edges
		for (let y = 0; y < height; y += sampleStep) {
			if (data[y]?.[0] !== undefined) perimeterSamples.push(data[y][0])
			if (data[y]?.[width - 1] !== undefined) perimeterSamples.push(data[y][width - 1])
		}

		// Calculate base elevation (median of perimeter to be robust against outliers)
		perimeterSamples.sort((a, b) => a - b)
		const baseElev = perimeterSamples.length > 0
			? perimeterSamples[Math.floor(perimeterSamples.length / 2)]
			: minElev

		// Calculate prominence (how much the peak rises above surrounding terrain)
		const prominence = peakElev - baseElev
		if (prominence <= 0) return 0.35

		// Threshold: find radius where elevation drops to base + 15% of prominence
		// This captures the "foot" of the volcano
		const threshold = baseElev + prominence * 0.15

		// Sample radially outward from center to find where elevation drops below threshold
		const numRays = 16
		const maxRadius = Math.min(width, height) / 2
		const rayDistances: number[] = []

		for (let ray = 0; ray < numRays; ray++) {
			const angle = (ray / numRays) * Math.PI * 2
			const dx = Math.cos(angle)
			const dy = Math.sin(angle)

			// Walk outward along this ray
			let foundRadius = maxRadius
			for (let r = 10; r < maxRadius; r += 5) {
				const sampleX = Math.round(centerX + dx * r)
				const sampleY = Math.round(centerY + dy * r)

				if (sampleX < 0 || sampleX >= width || sampleY < 0 || sampleY >= height) {
					foundRadius = r
					break
				}

				const elev = data[sampleY]?.[sampleX] ?? 0
				if (elev <= threshold) {
					foundRadius = r
					break
				}
			}
			rayDistances.push(foundRadius)
		}

		// Use the 75th percentile radius to be robust against valleys
		rayDistances.sort((a, b) => a - b)
		const effectiveRadius = rayDistances[Math.floor(rayDistances.length * 0.75)]

		// Add 50% padding for context, convert to fraction of grid size
		const paddedRadius = effectiveRadius * 1.5
		const gridSize = Math.min(width, height)
		let cropFraction = paddedRadius / gridSize

		// Clamp to reasonable range
		cropFraction = Math.max(0.15, Math.min(0.5, cropFraction))

		return cropFraction
	}

	/**
	 * Generate contour levels based on elevation range
	 */
	static generateContourLevels(minElev: number, maxElev: number, count: number = 10): number[] {
		if (maxElev <= minElev) {
			return []
		}

		// Round to nice intervals
		const range = maxElev - minElev
		let interval = range / count

		// Round interval to nice number
		const magnitude = Math.pow(10, Math.floor(Math.log10(interval)))
		const normalized = interval / magnitude

		if (normalized < 1.5) {
			interval = magnitude
		} else if (normalized < 3.5) {
			interval = 2 * magnitude
		} else if (normalized < 7.5) {
			interval = 5 * magnitude
		} else {
			interval = 10 * magnitude
		}

		const levels: number[] = []
		const start = Math.ceil(minElev / interval) * interval
		for (let level = start; level <= maxElev; level += interval) {
			levels.push(level)
		}

		return levels
	}
}

